import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../../service/sendRequest';
import consumerApi, { AUTH_SHAPE } from '../../../service/consumerApi';
import { enqueueSnackbar, somethingWrongNotification } from '../../notifier/notifierActions';
import { clearConsumerSession, setConsumerSession } from '../consumerReducer';
import { pickCredentials } from '../consumerSession';
import { ASSISTANTS_CATEGORY, PROJECT_TYPES, REACTIONS, SCOUT_CREATE_TYPE, SCOUT_TABS } from '../../../constants';
import { downloadDocument } from '../../../utils/downloadDocument';
import { changeLocalMessage, likeLocalMessage, setEditableMessage, setIsDirty } from '../../scout/scoutReducer';
import { openProject } from '../../global/globalReducer';
import generateFileName from '../../../utils/generateFileName';

const GET_ALL_MESSAGES_CONSUMER = 'consumer/scout/GET_ALL_MESSAGES_CONSUMER';
const GET_SELECTED_MESSAGES_CONSUMER = 'consumer/scout/GET_SELECTED_MESSAGES_CONSUMER';
const GET_FINAL_MESSAGES_CONSUMER = 'consumer/scout/GET_FINAL_MESSAGES_CONSUMER';
const GET_SCREENED_MESSAGES_CONSUMER = 'consumer/scout/GET_SCREENED_MESSAGES_CONSUMER';
const RESET_SESSION_CONSUMER = 'consumer/scout/RESET_SESSION_CONSUMER';
const REPLY_SCOUT_CONSUMER = 'consumer/scout/REPLY_SCOUT_CONSUMER';
const GENERATE_MESSAGES_CONSUMER = 'consumer/scout/GENERATE_MESSAGES_CONSUMER';
const GET_MESSAGES_BY_TAB_CONSUMER = 'consumer/scout/GET_MESSAGES_BY_TAB_CONSUMER';
const GENERATE_MORE_CONSUMER = 'consumer/scout/GENERATE_MORE_CONSUMER';
const COMPRESS_MESSAGES_CONSUMER = 'consumer/scout/COMPRESS_MESSAGES_CONSUMER';
const LIKE_MESSAGE_CONSUMER = 'consumer/scout/LIKE_MESSAGE_CONSUMER';
const DELETE_MESSAGE_CONSUMER = 'consumer/scout/DELETE_MESSAGE_CONSUMER';
const SELECT_MESSAGE_CONSUMER = 'consumer/scout/SELECT_MESSAGE_CONSUMER';
const GET_MESSAGES_LOGS_CONSUMER = 'consumer/scout/GET_MESSAGES_LOGS_CONSUMER';
const SAVE_MESSAGE_CONSUMER = 'consumer/scout/SAVE_MESSAGE_CONSUMER';
const DELETE_MULTIPLE_MESSAGES_CONSUMER = 'consumer/scout/DELETE_MULTIPLE_MESSAGES_CONSUMER';
const CREATE_MESSAGE_CONSUMER = 'consumer/scout/CREATE_MESSAGE_CONSUMER';
const EXPORT_MESSAGES_CONSUMER = 'consumer/scout/EXPORT_MESSAGES_CONSUMER';
const SEND_RESPONSE_DATA_CONSUMER = 'consumer/scout/SEND_RESPONSE_DATA_CONSUMER';
const REQUEST_FOR_HELP_CONSUMER = 'consumer/scout/REQUEST_FOR_HELP_CONSUMER';
const GET_VARIATION_SCORE = 'consumer/scout/GET_VARIATION_SCORE';
const CONSUMER_SCOUT_FINAL_MESSAGES = 'consumer/genie/CONSUMER_SCOUT_FINAL_MESSAGES';
const GET_SCOUT_DATA = 'consumer/scoutv2/GET_SCOUT_DATA';

export const getConsumerScoutData = createAsyncThunk(
  GET_SCOUT_DATA,
  async ({ projectName, projectPassword }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout`, null, {
          authShape: AUTH_SHAPE.FLAT,
          consumerCredentials: { projectName, projectPassword },
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(
      setConsumerSession({
        projectType: PROJECT_TYPES.SCOUTV2,
        projectName,
        projectPassword,
      }),
    );
    dispatch(openProject(projectName));

    return response.data;
  },
);

const loadConsumerMessages = async (getState, options = {}) => {
  const { sort, searchQuery } = getState().scout.tableSettings;

  const requestBody = {
    ...(searchQuery ? { word: searchQuery } : {}),
    ...options,
  };

  const response = await sendRequest((cancelToken) =>
    consumerApi.post(`/api/consumer/scout/messages/search?sort=${sort}`, requestBody, {
      authShape: AUTH_SHAPE.PROJECT_REQUEST,
      cancelToken,
    }),
  );

  return response.data;
};

export const getAllMessagesConsumer = createAsyncThunk(GET_ALL_MESSAGES_CONSUMER, async (_, { getState }) =>
  loadConsumerMessages(getState, { createType: SCOUT_CREATE_TYPE.GENERATED }),
);
export const getSelectedMessagesConsumer = createAsyncThunk(GET_SELECTED_MESSAGES_CONSUMER, async (_, { getState }) =>
  loadConsumerMessages(getState, { selected: true, createType: SCOUT_CREATE_TYPE.GENERATED }),
);
export const getFinalMessagesConsumer = createAsyncThunk(
  GET_FINAL_MESSAGES_CONSUMER,
  async (_, { getState, dispatch }) => {
    dispatch(getConsumerVariationScore());

    const compressedMessages = await loadConsumerMessages(getState, {
      reaction: REACTIONS.LIKE,
      createType: SCOUT_CREATE_TYPE.COMPRESSED,
    });
    const likedMessages = await loadConsumerMessages(getState, {
      reaction: REACTIONS.LIKE,
      createType: SCOUT_CREATE_TYPE.GENERATED,
    });

    return [...compressedMessages, ...likedMessages];
  },
);

export const getScreenedMessagesConsumer = createAsyncThunk(GET_SCREENED_MESSAGES_CONSUMER, async (_, { getState }) => {
  const compressedMessages = await loadConsumerMessages(getState, {
    reactionNot: REACTIONS.LIKE,
    createType: SCOUT_CREATE_TYPE.COMPRESSED,
  });
  const generatedMessages = await loadConsumerMessages(getState, {
    reactionNot: REACTIONS.LIKE,
    selected: true,
    createType: SCOUT_CREATE_TYPE.GENERATED,
  });
  return [...compressedMessages, ...generatedMessages];
});

export const resetSessionConsumer = createAsyncThunk(
  RESET_SESSION_CONSUMER,
  async (_, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/reset/session`, null, {
          authShape: AUTH_SHAPE.FLAT,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return response.data;
  },
);

export const replyScoutConsumer = createAsyncThunk(
  REPLY_SCOUT_CONSUMER,
  async ({ reply }, { rejectWithValue, dispatch, getState }) => {
    const scoutTab = getState().scout.scoutTab;
    const requestBody = { reply };

    let response;

    if (scoutTab === SCOUT_TABS.ALL_MESSAGES) {
      response = await sendRequest(
        () =>
          consumerApi.post(`/api/consumer/scout/reply`, requestBody, {
            authShape: AUTH_SHAPE.PROJECT_REQUEST,
          }),
        {
          onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
        },
      );
    }

    if (scoutTab === SCOUT_TABS.SELECTED_MESSAGES) {
      response = await sendRequest(
        () =>
          consumerApi.post(`/api/consumer/scout/reply/selected`, requestBody, {
            authShape: AUTH_SHAPE.PROJECT_REQUEST,
          }),
        {
          onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
        },
      );
    }

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const generateMessagesConsumer = createAsyncThunk(
  GENERATE_MESSAGES_CONSUMER,
  async ({ values }, { rejectWithValue, dispatch }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_ALL].assistant?.id;

    if (!assistantId || !url) {
      return rejectWithValue(null);
    }

    const requestBody = {
      assistantId,
      byPass,
      focusOn,
      url,
      fileText,
    };

    const response = await sendRequest(
      (cancelToken) =>
        consumerApi.post(`/api/consumer/scout/generate/messages`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
          cancelToken,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const getMessagesByTab = createAsyncThunk(
  GET_MESSAGES_BY_TAB_CONSUMER,
  async (_, { getState, dispatch, rejectWithValue }) => {
    const scoutTab = getState().scout.scoutTab;

    if (!pickCredentials(getState().consumer.session)) {
      return rejectWithValue(null);
    }

    if (scoutTab === SCOUT_TABS.ALL_MESSAGES) {
      dispatch(getAllMessagesConsumer());
    }

    if (scoutTab === SCOUT_TABS.SELECTED_MESSAGES) {
      dispatch(getSelectedMessagesConsumer());
    }

    if (scoutTab === SCOUT_TABS.FINAL_MESSAGES) {
      dispatch(getFinalMessagesConsumer());
    }

    if (scoutTab === SCOUT_TABS.SCREENED_MESSAGES) {
      dispatch(getScreenedMessagesConsumer());
    }
  },
);

export const generateMoreConsumer = createAsyncThunk(
  GENERATE_MORE_CONSUMER,
  async ({ values }, { rejectWithValue, dispatch }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_SELECTED].assistant?.id;

    if (!assistantId || !url) {
      return rejectWithValue(null);
    }

    const requestBody = {
      assistantId,
      byPass,
      focusOn,
      url,
      fileText,
    };

    const response = await sendRequest(
      (cancelToken) =>
        consumerApi.post(`/api/consumer/scout/generate/selected/messages`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
          cancelToken,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const compressMessagesConsumer = createAsyncThunk(
  COMPRESS_MESSAGES_CONSUMER,
  async ({ values }, { rejectWithValue, dispatch, getState }) => {
    const count = getState().scout.targetNumber;
    const { channels } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.COMPRESS].assistant?.id;

    if (!assistantId || !pickCredentials(getState().consumer.session)) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/compress?assistantId=${assistantId}&count=${count}`, null, {
          authShape: AUTH_SHAPE.FLAT,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(getMessagesByTab());

    return response.data;
  },
);

export const likeMessageConsumer = createAsyncThunk(
  LIKE_MESSAGE_CONSUMER,
  async ({ id, reaction, createType }, { dispatch, rejectWithValue }) => {
    dispatch(likeLocalMessage({ id, reaction, createType }));

    const requestBody = {
      id,
      ...(reaction ? { reaction } : {}),
    };

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/messages/reaction`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );
  },
);

export const deleteMessageConsumer = createAsyncThunk(DELETE_MESSAGE_CONSUMER, async ({ id }, { dispatch }) => {
  await sendRequest(
    () =>
      consumerApi.delete(`/api/consumer/scout/messages`, {
        data: id,
        authShape: AUTH_SHAPE.PROJECT_REQUEST,
      }),
    {
      onError: () =>
        dispatch(
          enqueueSnackbar({
            message: 'couldntDeleteMessage',
            options: {
              variant: 'error',
            },
          }),
        ),
    },
  );

  dispatch(
    enqueueSnackbar({
      message: 'messageHasBeenDeleted',
      options: {
        variant: 'success',
      },
    }),
  );

  return id;
});

export const selectMessageConsumer = createAsyncThunk(
  SELECT_MESSAGE_CONSUMER,
  async ({ id, selected }, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/messages/${selected ? 'unselect' : 'select'}`, id, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: () => {
          dispatch(somethingWrongNotification());
          return rejectWithValue({ id, selected });
        },
      },
    );

    return { id, selected: !selected, createType: response.data.createType };
  },
);

export const getMessageLogsConsumer = createAsyncThunk(
  GET_MESSAGES_LOGS_CONSUMER,
  async ({ messageId }, { rejectWithValue, dispatch }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/messages/history`, messageId, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    return response.data;
  },
);

export const saveMessageChangesConsumer = createAsyncThunk(
  SAVE_MESSAGE_CONSUMER,
  async (_, { dispatch, getState, rejectWithValue }) => {
    const {
      editableMessage: { id, message },
    } = getState().scout;

    dispatch(changeLocalMessage({ id, message }));
    dispatch(setEditableMessage({ id: null, message: '' }));
    dispatch(setIsDirty(false));

    const requestBody = { message };

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/messages/edit-message/${id}`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );
  },
);

export const createMessageConsumer = createAsyncThunk(
  CREATE_MESSAGE_CONSUMER,
  async ({ messages }, { dispatch, rejectWithValue }) => {
    const messagesToCreate = messages.map(({ message }) => message);

    if (!messagesToCreate.length) {
      return rejectWithValue(null);
    }

    const requestBody = messagesToCreate;

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/messages/create`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        successStatusCode: 201,
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'messageWasCreatedSuccessfully',
        options: {
          variant: 'success',
        },
      }),
    );

    dispatch(getMessagesByTab());
  },
);

export const deleteMultipleMessagesConsumer = createAsyncThunk(
  DELETE_MULTIPLE_MESSAGES_CONSUMER,
  async (ids, { dispatch, rejectWithValue }) => {
    const requestBody = ids;

    await sendRequest(
      () =>
        consumerApi.delete(`/api/consumer/scout/messages/delete/by/ids`, {
          data: requestBody,
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => {
          dispatch(
            enqueueSnackbar({
              message: 'couldntDeleteMessages',
              options: {
                variant: 'error',
              },
            }),
          );
          return rejectWithValue(e);
        },
      },
    );

    dispatch(
      enqueueSnackbar({
        message: 'messagesHasBeenDeleted',
        options: {
          variant: 'success',
        },
      }),
    );

    return [];
  },
);

export const exportMessagesConsumer = createAsyncThunk(
  EXPORT_MESSAGES_CONSUMER,
  async (_, { rejectWithValue, getState, dispatch }) => {
    const messages = getState().scout.messagesContent.map(({ id }) => id);
    const projectName = getState().scout.projectName;

    if (!messages.length) {
      dispatch(
        enqueueSnackbar({
          message: 'noMessagesAvailable',
          options: {
            variant: 'warning',
            subMessage: 'pleaseGenerateMessagesBefore',
          },
        }),
      );
      return rejectWithValue(null);
    }

    const requestBody = messages;

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/messages/export`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
          responseType: 'blob',
        }),
      {
        onError: (e) => defaultErrorHandler(e, dispatch, rejectWithValue),
      },
    );

    const fileName = generateFileName({ name: projectName, ext: 'txt' });

    downloadDocument(fileName, response.data);
  },
);

export const sendResponseDataConsumer = createAsyncThunk(
  SEND_RESPONSE_DATA_CONSUMER,
  async (values, { rejectWithValue, dispatch, getState }) => {
    const {
      ageFrom,
      ageTo,
      company,
      email,
      gender,
      market,
      otherRequirements,
      region,
      respondentVolume,
      surveyTemplate,
    } = values;
    const messagesIds = getState().scout.collectDataMessages.map(({ id }) => id);

    const requestBody = {
      ageFrom,
      ageTo,
      company,
      email,
      gender,
      market,
      otherRequirements,
      region,
      respondentVolume,
      surveyTemplate,
      messagesIds,
    };

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/send`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
        onSuccess: () => {
          dispatch(
            enqueueSnackbar({
              message: 'emailHasBeenSent',
              options: {
                variant: 'success',
              },
            }),
          );
        },
      },
    );
  },
);

export const requestForHelpConsumer = createAsyncThunk(
  REQUEST_FOR_HELP_CONSUMER,
  async (values, { rejectWithValue, dispatch }) => {
    const { email, question } = values;

    const requestBody = { email, question };

    await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/help`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
        }),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
        onSuccess: () => {
          dispatch(
            enqueueSnackbar({
              message: 'helpRequestSubmitted',
              options: {
                variant: 'success',
                subMessage: 'weeWillTouchYou',
              },
            }),
          );
        },
      },
    );
  },
);

export const consumerScoutFinalMessages = createAsyncThunk(
  CONSUMER_SCOUT_FINAL_MESSAGES,
  async ({ projectName, projectPassword }, { rejectWithValue, dispatch }) => {
    if (!projectName || !projectPassword) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/messages/final/messages`, null, {
          authShape: AUTH_SHAPE.FLAT,
          consumerCredentials: { projectName, projectPassword },
        }),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          dispatch(clearConsumerSession());
          return rejectWithValue(e);
        },
      },
    );

    dispatch(
      setConsumerSession({
        projectType: PROJECT_TYPES.SCOUTV2,
        projectName,
        projectPassword,
      }),
    );
    return response.data;
  },
);

export const getConsumerVariationScore = createAsyncThunk(
  GET_VARIATION_SCORE,
  async (_, { rejectWithValue, getState }) => {
    if (!pickCredentials(getState().consumer.session)) {
      return rejectWithValue(null);
    }

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/scout/variation/score`, null, {
          authShape: AUTH_SHAPE.FLAT,
        }),
      {
        onError: rejectWithValue(null),
      },
    );

    return response.data;
  },
);

const defaultErrorHandler = (e, dispatch, rejectWithValue) => {
  if (e.response.status === 409) {
    dispatch(
      enqueueSnackbar({
        message: 'projectInUse',
        options: {
          variant: 'warning',
          subMessage: 'thisProjectIsCurrentlyUsing',
          subMessageParams: {
            username: e.response.data.username ?? '',
          },
        },
      }),
    );
    dispatch(clearConsumerSession());

    return rejectWithValue(e);
  }

  dispatch(somethingWrongNotification());
  return rejectWithValue(e);
};
